import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, FileText } from 'lucide-react';
import { printInvoice } from '../../utils/printInvoice';
import { invoiceToPrintable } from './WholesaleTab';

const TRADE_PRODUCTS = [
  { name: 'Golden Morning', size: '500g', price: 9.95 },
  { name: 'River Bloom', size: '500g', price: 10.95 },
  { name: 'Wild Forest', size: '500g', price: 11.95 },
];

const CreateInvoiceModal = ({ onClose, password, onSaved, invoice }) => {
  const editing = !!invoice;
  const [invoiceNo, setInvoiceNo] = useState(invoice?.invoice_no || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wholesale/invoices/next-number?password=${encodeURIComponent(password)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.invoice_no) setInvoiceNo(prev => prev || d.invoice_no); })
      .catch(() => {});
  }, [editing, password]);
  const [saveError, setSaveError] = useState('');
  const [customer, setCustomer] = useState({
    name: invoice?.customer_name || '',
    email: invoice?.customer_email || '',
    phone: invoice?.customer_phone || '',
    street: invoice?.billing_address?.street || '',
    street2: invoice?.billing_address?.street2 || '',
    suburb: invoice?.billing_address?.suburb || '',
    state: invoice?.billing_address?.state || '',
    postcode: invoice?.billing_address?.postcode || ''
  });
  const [items, setItems] = useState(invoice?.items ? invoice.items.map(i => ({ ...i })) : []);
  const [shipping, setShipping] = useState(invoice ? String(invoice.shipping || 0) : '0');
  const [discount, setDiscount] = useState(invoice ? String(invoice.discount || 0) : '0');
  const [paid, setPaid] = useState(invoice?.payment_paid || false);
  const [terms, setTerms] = useState(invoice?.payment_terms || 'Payment due within 14 days of invoice date.');

  const setField = (k, v) => setCustomer(prev => ({ ...prev, [k]: v }));
  const addItem = (preset) => setItems(prev => [...prev, preset ? { ...preset, quantity: 12 } : { name: '', size: '500g', quantity: 1, price: 0 }]);
  const updateItem = (i, k, v) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, it) => s + (parseFloat(it.price) || 0) * (parseInt(it.quantity) || 0), 0);
  const total = subtotal - (parseFloat(discount) || 0) + (parseFloat(shipping) || 0);

  const handleSave = async (printAfter) => {
    setSaving(true);
    setSaveError('');
    try {
      const url = editing
        ? `${process.env.REACT_APP_BACKEND_URL}/api/wholesale/invoices/${encodeURIComponent(invoice.invoice_no)}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/wholesale/invoices`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          invoice_no: editing ? invoice.invoice_no : (invoiceNo.trim() || null),
          customer_name: customer.name,
          customer_email: customer.email,
          customer_phone: customer.phone,
          billing_address: { street: customer.street, street2: customer.street2, suburb: customer.suburb, state: customer.state, postcode: customer.postcode },
          items: items.map(it => ({ name: it.name, size: it.size, quantity: parseInt(it.quantity) || 0, price: parseFloat(it.price) || 0 })),
          subtotal,
          discount: parseFloat(discount) || 0,
          shipping: parseFloat(shipping) || 0,
          total,
          payment_paid: paid,
          payment_terms: terms,
        })
      });
      if (!res.ok) {
        setSaveError(res.status === 409 ? `Invoice number ${invoiceNo.trim()} already exists. Please use a different number.` : 'Failed to save invoice. Please try again.');
        setSaving(false);
        return;
      }
      const saved = await res.json();
      if (printAfter) printInvoice(invoiceToPrintable(saved));
      if (onSaved) onSaved();
      onClose();
    } catch {
      setSaveError('Failed to save invoice. Please try again.');
    }
    setSaving(false);
  };

  const inputCls = "w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-invoice-modal">
      <div className="bg-background rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{editing ? `Edit Invoice ${invoice.invoice_no}` : 'Create Invoice'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="close-invoice-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-xs font-medium text-foreground/70 mb-1">Invoice Number</label>
            <input value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} placeholder="Auto (e.g. RB-W-0001)" disabled={editing} className={`${inputCls} ${editing ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid="invoice-number-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/70 mb-1">Business / Customer Name *</label>
            <input value={customer.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. The Corner Grocer" className={inputCls} data-testid="invoice-customer-name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/70 mb-1">Email</label>
            <input value={customer.email} onChange={e => setField('email', e.target.value)} className={inputCls} data-testid="invoice-customer-email" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/70 mb-1">Phone</label>
            <input value={customer.phone} onChange={e => setField('phone', e.target.value)} className={inputCls} data-testid="invoice-customer-phone" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/70 mb-1">Street Address</label>
            <input value={customer.street} onChange={e => setField('street', e.target.value)} className={inputCls} data-testid="invoice-street" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/70 mb-1">Address Line 2</label>
            <input value={customer.street2} onChange={e => setField('street2', e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-3 gap-2 md:col-span-2">
            <input value={customer.suburb} onChange={e => setField('suburb', e.target.value)} placeholder="Suburb" className={inputCls} />
            <input value={customer.state} onChange={e => setField('state', e.target.value)} placeholder="State" className={inputCls} />
            <input value={customer.postcode} onChange={e => setField('postcode', e.target.value)} placeholder="Postcode" className={inputCls} />
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-foreground">Line Items</label>
            <div className="flex flex-wrap gap-2">
              {TRADE_PRODUCTS.map(p => (
                <button key={p.name} onClick={() => addItem(p)}
                  className="px-3 py-1.5 rounded-lg border border-primary/40 text-primary text-xs font-medium hover:bg-primary/5 transition-colors"
                  data-testid={`quick-add-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                  + {p.name} (${p.price.toFixed(2)})
                </button>
              ))}
              <button onClick={() => addItem()} className="px-3 py-1.5 rounded-lg border border-border text-foreground/70 text-xs font-medium hover:bg-muted transition-colors inline-flex items-center space-x-1" data-testid="add-blank-item">
                <Plus className="w-3 h-3" /><span>Custom Item</span>
              </button>
            </div>
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-foreground/50 py-4 text-center border border-dashed border-border rounded-xl">Add items using the buttons above (trade prices pre-filled)</p>
          ) : (
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-item-row-${i}`}>
                  <input value={it.name} onChange={e => updateItem(i, 'name', e.target.value)} placeholder="Product name" className={`${inputCls} col-span-4`} />
                  <input value={it.size} onChange={e => updateItem(i, 'size', e.target.value)} placeholder="Size" className={`${inputCls} col-span-2`} />
                  <input type="number" min="1" value={it.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className={`${inputCls} col-span-2`} data-testid={`item-qty-${i}`} />
                  <input type="number" step="0.01" value={it.price} onChange={e => updateItem(i, 'price', e.target.value)} className={`${inputCls} col-span-2`} data-testid={`item-price-${i}`} />
                  <span className="col-span-1 text-sm font-medium text-foreground text-right">${((parseFloat(it.price) || 0) * (parseInt(it.quantity) || 0)).toFixed(2)}</span>
                  <button onClick={() => removeItem(i)} className="col-span-1 p-2 text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-item-${i}`}>
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-xs font-medium text-foreground/70 mb-1">Shipping / Freight ($)</label>
            <input type="number" step="0.01" value={shipping} onChange={e => setShipping(e.target.value)} className={inputCls} data-testid="invoice-shipping-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground/70 mb-1">Discount ($)</label>
            <input type="number" step="0.01" value={discount} onChange={e => setDiscount(e.target.value)} className={inputCls} data-testid="invoice-discount-input" />
          </div>
        </div>

        <div className="mb-6 p-4 rounded-xl bg-muted/30 border border-border">
          <div className="flex items-center space-x-4 mb-3">
            <label className="flex items-center space-x-2 text-sm cursor-pointer">
              <input type="radio" checked={!paid} onChange={() => setPaid(false)} data-testid="invoice-status-due" />
              <span>Payment Due</span>
            </label>
            <label className="flex items-center space-x-2 text-sm cursor-pointer">
              <input type="radio" checked={paid} onChange={() => setPaid(true)} data-testid="invoice-status-paid" />
              <span>Paid in Full</span>
            </label>
          </div>
          {!paid && (
            <div>
              <label className="block text-xs font-medium text-foreground/70 mb-1">Payment Terms</label>
              <input value={terms} onChange={e => setTerms(e.target.value)} className={inputCls} data-testid="invoice-terms-input" />
              <p className="text-xs text-foreground/50 mt-2">Bank details (Ricky's Bees · BSB 083 004 · Acct 365 643 471) are printed automatically on unpaid invoices.</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-foreground/70">
            Subtotal: <strong className="text-foreground">${subtotal.toFixed(2)}</strong>
            <span className="mx-2">·</span>
            Total: <strong className="text-foreground text-lg">${total.toFixed(2)}</strong>
          </div>
          <div className="flex items-center space-x-2">
            {editing && (
              <button onClick={() => handleSave(false)} disabled={saving || items.length === 0 || !customer.name.trim()}
                className="px-5 py-3 rounded-full border-2 border-primary text-primary hover:bg-primary/5 transition-colors font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="save-invoice-button">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            <button onClick={() => handleSave(true)} disabled={saving || items.length === 0 || !customer.name.trim()}
              className="inline-flex items-center space-x-2 px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="generate-invoice-button">
              <FileText className="w-4 h-4" /><span>{saving ? 'Saving...' : editing ? 'Save & Print' : 'Save & Generate Invoice'}</span>
            </button>
          </div>
        </div>
        {saveError && <p className="text-red-500 text-sm mt-3 text-right" data-testid="invoice-save-error">{saveError}</p>}
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
